import { useNavigate } from 'react-router-dom';
import type { ChatConversationWithParticipants } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { resolveUploadUrl } from '@/lib/uploads';

interface ConversationListProps {
  conversations: ChatConversationWithParticipants[];
  activeId?: string;
  isLoading?: boolean;
}

function formatTimeAgo(dateStr: string | Date | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}g`;
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

export function ConversationList({ conversations, activeId, isLoading }: ConversationListProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-200 border-t-brand-teal" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
          <svg className="h-6 w-6 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <div>
          <p className="text-[13px] font-medium text-neutral-400">Nessuna conversazione</p>
          <p className="mt-1 text-[11px] text-neutral-400">
            Avvia una chat dal profilo di un consulente
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {conversations.map((conv) => {
        const isActive = conv.id === activeId;
        const currentUserId = user?.id;
        const isCurrentUserTheUser = currentUserId === String(conv.userId);

        const otherParty = isCurrentUserTheUser
          ? {
              firstName: conv.professional?.firstName ?? '',
              lastName: conv.professional?.lastName ?? '',
              avatar: conv.professional?.avatar,
              subtitle: conv.professional?.title,
            }
          : {
              firstName: conv.user?.firstName ?? '',
              lastName: conv.user?.lastName ?? '',
              avatar: conv.user?.avatar,
              subtitle: undefined,
            };

        const fullName = `${otherParty.firstName} ${otherParty.lastName}`.trim() || 'Consulente';

        const unread = isCurrentUserTheUser
          ? conv.unreadCountUser
          : conv.unreadCountProfessional;

        return (
          <button
            key={conv.id}
            onClick={() => navigate(`/my/chat/${conv.id}`)}
            className={`group flex w-full items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
              isActive
                ? 'bg-brand-teal/[0.08] border-l-[3px] border-brand-teal'
                : 'border-l-[3px] border-transparent hover:bg-neutral-50'
            }`}
          >
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="relative h-10 w-10">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100">
                  <img src="/images/logo-navy.png" alt="" className="h-6 w-6 object-contain opacity-60" />
                </div>
                {otherParty.avatar && (
                  <img
                    src={resolveUploadUrl(otherParty.avatar)}
                    alt={fullName}
                    className="absolute inset-0 h-10 w-10 rounded-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
              </div>
              {unread > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-orange px-1 text-[9px] font-bold leading-none text-white ring-2 ring-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className={`truncate text-[13px] font-semibold ${unread > 0 ? 'text-neutral-900' : 'text-neutral-700'}`}>
                  {fullName}
                </span>
                <span className="shrink-0 text-[10px] tabular-nums text-neutral-400">
                  {formatTimeAgo(conv.lastMessageAt)}
                </span>
              </div>
              {otherParty.subtitle && (
                <p className="truncate text-[11px] text-neutral-400">{otherParty.subtitle}</p>
              )}
              {conv.lastMessagePreview && (
                <p className={`mt-0.5 truncate text-[11px] leading-snug ${unread > 0 ? 'font-medium text-neutral-600' : 'text-neutral-400'}`}>
                  {conv.lastMessagePreview}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
